import React, { useState, ChangeEvent, FormEvent, useContext, forwardRef } from 'react';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone, faLocationDot, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { appContext } from "../../App";

interface FormData {
    fullName: string;
    email: string;
    message: string;
    phone: string;
}

interface FormErrors {
    fullName?: string;
    email?: string;
    message?: string;
    phone?: string;
}

export const Contact = forwardRef<HTMLDivElement>((props, ref) => {
    const { darkMode } = useContext(appContext);
    const [formData, setFormData] = useState<FormData>({
        fullName: '',
        email: '',
        message: '',
        phone: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    const validate = (): FormErrors => {
        const tempErrors: FormErrors = {};
        if (!formData.fullName.trim()) tempErrors.fullName = 'Name is required';
        if (!formData.email.trim()) {
            tempErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            tempErrors.email = 'Please enter a valid email';
        }
        if (!formData.message.trim()) tempErrors.message = 'Message is required';
        if (!formData.phone.trim()) tempErrors.phone = 'Phone is required';
        else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
            tempErrors.phone = 'Please enter a valid number';
        }
        return tempErrors;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const validationErrors = validate();
        
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        
        // Simulate API call
        setTimeout(() => {
            const { fullName, email, message, phone } = formData;
            const whatsappMessage = `
                *New Mining Inquiry*%0A
                %0A
                *Name:* ${fullName}%0A
                *Email:* ${email}%0A
                *Phone:* ${phone}%0A
                %0A
                *Message:*%0A
                ${message}
            `.trim();
            
            const whatsappURL = `https://wa.me/971542669478?text=${whatsappMessage}`;
            window.open(whatsappURL, '_blank');

            // Reset form
            setFormData({ fullName: '', email: '', message: '', phone: '' });
            setErrors({});
            setIsSubmitting(false);
        }, 1000);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => {
        if (typeof e === 'string') {
            setFormData(prev => ({ ...prev, phone: e }));
            if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
        } else {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
            if (errors[name as keyof FormErrors]) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }
        }
    };

    return (
        <section ref={ref} className="py-20 relative min-h-screen flex items-center justify-center">
            <div className="container mx-auto px-4 max-w-6xl">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                        Start <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">Mining Today</span>
                    </h1>
                    
                    <p className="text-lg text-slate-600 dark:text-slate-300 font-medium">
                        Get a free consultation and discover how much you can earn.
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-12 items-start">
                    {/* Contact Info - Minimal */}
                    <div className="w-full lg:w-2/5">
                        <div className="space-y-8">
                            <div className="p-6 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faPhone} className="text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Call Us</p>
                                        <p className="font-bold text-slate-900 dark:text-white">+971542669478</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Email Us</p>
                                        <a href="mailto:asil@segments.ae" className="font-bold text-slate-900 dark:text-white hover:text-blue-500 transition-colors">
                                            asil@segments.ae
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form - Minimal */}
                    <div className="w-full lg:w-3/5">
                        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-lg">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <input
                                            className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl border ${errors.fullName ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} focus:border-blue-500 focus:outline-none transition-colors`}
                                            placeholder="Your Name"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            type="text"
                                        />
                                        {errors.fullName && <p className="text-red-500 text-xs mt-1 ml-1">{errors.fullName}</p>}
                                    </div>
                                    <div>
                                        <input
                                            className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl border ${errors.email ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} focus:border-blue-500 focus:outline-none transition-colors`}
                                            placeholder="Your Email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            type="email"
                                        />
                                        {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
                                    </div>
                                </div>

                                <div>
                                    <PhoneInput
                                        country={'ae'}
                                        value={formData.phone}
                                        onChange={handleChange}
                                        containerClass="!w-full"
                                        inputClass={`!w-full !h-[52px] !px-4 !pl-14 !bg-slate-50 dark:!bg-slate-800 !text-slate-900 dark:!text-white !rounded-xl !border ${errors.phone ? '!border-red-500' : '!border-slate-200 dark:!border-slate-700'} focus:!border-blue-500 !outline-none`}
                                        buttonClass="!bg-transparent !border-none !rounded-l-xl !pl-4"
                                        dropdownClass="dark:!bg-slate-900 dark:!text-white"
                                        placeholder="Phone Number"
                                    />
                                    {errors.phone && <p className="text-red-500 text-xs mt-1 ml-1">{errors.phone}</p>}
                                </div>

                                <div>
                                    <textarea
                                        className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl border ${errors.message ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} focus:border-blue-500 focus:outline-none transition-colors h-32 resize-none`}
                                        placeholder="Tell us about your mining goals or questions..."
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                    />
                                    {errors.message && <p className="text-red-500 text-xs mt-1 ml-1">{errors.message}</p>}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Get Free Consultation</span>
                                            <FontAwesomeIcon icon={faPaperPlane} className="text-sm" />
                                        </>
                                    )}
                                </button>

                                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                                    We'll respond within 15 minutes during business hours
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});